import React, { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Route, Switch, Router as WouterRouter } from "wouter";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ActivityProvider,
  type ActivityRuntimeAdapter,
} from "@/activity/activity";
import { AdsProvider, noopAdsAdapter, type AdsAdapter } from "@/activity/ads";

const Dashboard = lazy(() => import("@/pages/dashboard"));
const CompaniesPage = lazy(() => import("@/pages/companies"));
const CompanyDetail = lazy(() => import("@/pages/company-detail"));
const ResumesPage = lazy(() => import("@/pages/resumes"));
const ResumeDetail = lazy(() => import("@/pages/resume-detail"));
const AnalysesPage = lazy(() => import("@/pages/analyses"));
const NewAnalysisPage = lazy(() => import("@/pages/analysis-new"));
const AnalysisDetail = lazy(() => import("@/pages/analysis-detail"));
const InterviewsPage = lazy(() => import("@/pages/interviews"));
const NewInterviewPage = lazy(() => import("@/pages/interview-new"));
const InterviewDetail = lazy(() => import("@/pages/interview-detail"));
const CoverLetterPage = lazy(() => import("@/pages/cover-letter"));
const AiResultsPage = lazy(() => import("@/pages/ai-results"));
const AiResultDetailPage = lazy(() => import("@/pages/ai-result-detail"));
const LegalDocumentPage = lazy(() => import("@/pages/legal-document"));
const PrivacyPolicyPage = lazy(() =>
  import("@/pages/legal-document").then((module) => ({
    default: module.PrivacyPolicyPage,
  })),
);
const NotFound = lazy(() => import("@/pages/not-found"));

type RuntimeBoundaryProps = { children: React.ReactNode };

export type AppRuntime = {
  Shell: React.ComponentType<RuntimeBoundaryProps>;
  AuthBoundary: React.ComponentType<RuntimeBoundaryProps>;
  RootPage: React.ComponentType;
  LoginPage: React.ComponentType;
  RegisterPage: React.ComponentType;
  AdminLoginPage?: React.ComponentType;
  AdminPage?: React.ComponentType;
  SubscriptionPage?: React.ComponentType;
  AnalysisTicketsPage?: React.ComponentType;
  AnalysisTicketHistoryPage?: React.ComponentType;
  NewAnalysisPage?: React.ComponentType;
  activity: ActivityRuntimeAdapter;
  ads?: AdsAdapter;
  AdsProvider?: React.ComponentType<RuntimeBoundaryProps>;
};

export type RuntimeAppProps = {
  runtime: AppRuntime;
  queryClient: QueryClient;
};

function RouteFallback() {
  return (
    <div
      className="min-h-screen bg-slate-50 p-6"
      role="status"
      aria-label="화면 불러오는 중"
    >
      <div className="mx-auto max-w-5xl space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    </div>
  );
}

function ProtectedRouter({ runtime }: { runtime: AppRuntime }) {
  return (
    <Switch>
      <Route
        path="/dashboard"
        component={Dashboard}
      />
      <Route
        path="/companies"
        component={CompaniesPage}
      />
      <Route
        path="/companies/:id"
        component={CompanyDetail}
      />
      <Route
        path="/resumes"
        component={ResumesPage}
      />
      <Route
        path="/resumes/:id"
        component={ResumeDetail}
      />
      <Route
        path="/analyses"
        component={AnalysesPage}
      />
      <Route path="/analyses/new">
        {() => {
          const Page = runtime.NewAnalysisPage ?? NewAnalysisPage;
          return <Page />;
        }}
      </Route>
      <Route
        path="/analyses/:id"
        component={AnalysisDetail}
      />
      <Route
        path="/interviews"
        component={InterviewsPage}
      />
      <Route
        path="/interviews/new"
        component={NewInterviewPage}
      />
      <Route
        path="/interviews/:id"
        component={InterviewDetail}
      />
      <Route
        path="/cover-letter"
        component={CoverLetterPage}
      />
      <Route
        path="/ai-results/:taskId"
        component={AiResultDetailPage}
      />
      <Route
        path="/ai-results"
        component={AiResultsPage}
      />
      {runtime.SubscriptionPage && (
        <Route
          path="/subscription"
          component={runtime.SubscriptionPage}
        />
      )}
      {runtime.AnalysisTicketHistoryPage && (
        <Route
          path="/analysis-tickets/history"
          component={runtime.AnalysisTicketHistoryPage}
        />
      )}
      {runtime.AnalysisTicketsPage && (
        <Route
          path="/analysis-tickets"
          component={runtime.AnalysisTicketsPage}
        />
      )}
      <Route component={NotFound} />
    </Switch>
  );
}

function RuntimeRouter({ runtime }: { runtime: AppRuntime }) {
  const AuthBoundary = runtime.AuthBoundary;
  const Shell = runtime.Shell;
  const AdsInitializer = runtime.AdsProvider ?? React.Fragment;
  return (
    <Switch>
      <Route path="/" component={runtime.RootPage} />
      <Route path="/login" component={runtime.LoginPage} />
      <Route path="/register" component={runtime.RegisterPage} />
      <Route path="/service-terms" component={LegalDocumentPage} />
      <Route path="/privacy-policy" component={PrivacyPolicyPage} />
      {runtime.AdminLoginPage && <Route path="/admin/login" component={runtime.AdminLoginPage} />}
      {runtime.AdminPage && <Route path="/admin" component={runtime.AdminPage} />}
      <Route>
        <AuthBoundary>
          <AdsInitializer>
            <AdsProvider adapter={runtime.ads ?? noopAdsAdapter}>
              <ActivityProvider runtime={runtime.activity} adsAdapter={runtime.ads ?? noopAdsAdapter}>
                <Shell>
                  <Suspense fallback={<RouteFallback />}>
                    <ProtectedRouter runtime={runtime} />
                  </Suspense>
                </Shell>
              </ActivityProvider>
            </AdsProvider>
          </AdsInitializer>
        </AuthBoundary>
      </Route>
    </Switch>
  );
}

export function RuntimeApp({ runtime, queryClient }: RuntimeAppProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
          <Suspense fallback={<RouteFallback />}>
            <RuntimeRouter runtime={runtime} />
          </Suspense>
        </WouterRouter>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}
